import { useState, useRef, useEffect, MutableRefObject, useCallback } from 'react';
import { Box, Typography, TextField, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { AttachIcon, SendIcon } from 'assets';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { selectWaitingRoomProfile } from 'store/waitingRoom';
import { useApiRequests } from 'hooks/useApiRequests';
import { useChat, useChatState, useDialog } from 'hooks';
import { ADMIN, CLINIC_ADMIN, CLINIC_PROVIDER } from 'constants/roles';

import { DocumentsPickerDialog } from '../DocumentsPickerDialog/DocumentsPickerDialog';
import { MoreOptions } from '../MoreOptions/MoreOptions';

import styles from './ChatInput.module.scss';

const ALLOWED_FILE_TYPES =
  'audio/*, image/*, text/*, video/*, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document .xslx, .ppt, .pdf, .key, .svg, .csv, application/x-zip-compressed';

type ChatInputProps = {
  isGroup: boolean;
  onTyping(): void;
  sendMessage(message: string | FormData): Promise<number>;
};

export function ChatInput({ isGroup, onTyping, sendMessage }: ChatInputProps): JSX.Element {
  const { t, i18n } = useTranslation('providerRoom');
  const { uploadGuestChatDocument, markDocumentAsShared } = useApiRequests();
  const user = useAppSelector(selectUser);
  const waitingRoomProfile = useAppSelector(selectWaitingRoomProfile);
  const { identity } = useChat();
  const { typingUserName } = useChatState(isGroup);
  const { showDialog } = useDialog();
  const inputFile: MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);
  const [input, setInput] = useState<string>('');
  const [isSendingFile, setIsSendingFile] = useState<boolean>(false);
  const [sendingFileError, setSendingFileError] = useState<string | null>(null);
  const [typingDots, setTypingDots] = useState<string>('');

  useEffect(() => {
    if (typingUserName) {
      const timerId = setInterval(() => {
        setTypingDots((prevTypingDots: string) => {
          if (prevTypingDots.length >= 3) {
            return '';
          }

          return prevTypingDots + '.';
        });
      }, 500);

      return () => {
        clearInterval(timerId);
      };
    }
  }, [typingUserName]);

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setInput(event.target.value);
    onTyping();
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      handleSandMessage();
    }
  };

  const handleSandMessage = (): void => {
    if (input.trim()) {
      sendMessage(input.trim());
      setInput('');
    }
  };

  const handleSelectFile = useCallback((): void => {
    onTyping();
    inputFile.current?.click();
  }, [inputFile, onTyping]);

  const handleOpenDocuments = (): void => {
    const modal: { destroy: () => void } = showDialog(DocumentsPickerDialog, {
      onClose: () => modal.destroy(),
      onSelectDocument: async (doc) => {
        modal.destroy();

        const fileName = doc.path.split('/').pop() || t('chatInput.documentFallbackName');
        const path = doc.path.replace(`/${fileName}`, '');
        const fullPath = `${path}/${fileName}`;

        try {
          const size = doc.metadata?.size ?? 0;
          const sizeLabel =
            size >= 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} MB` : `${(size / 1024).toFixed(1)} KB`;

          // ✅ Use fullPath in chat message (NOT direct blob URL)
          await sendMessage(`[📎 Shared File] ${fileName}|${sizeLabel}|${fullPath}`);

          // ✅ Track Clinic Document as recently shared in localStorage
          if (fullPath.startsWith('Clinic/') && user?.clinicId) {
            const key = `clinicRecentDocs:${user.clinicId}`;
            const current = JSON.parse(localStorage.getItem(key) ?? '[]');

            if (!current.includes(fullPath)) {
              current.unshift(fullPath); // newest on top
              if (current.length > 10) current.pop(); // optional limit
              localStorage.setItem(key, JSON.stringify(current));
            }
          }

          if (!user?.clinicId || !user?.id) return;

          const personalPath = `Personal/${user.clinicId}/${user.id}`;
          await markDocumentAsShared(personalPath, fileName);
        } catch (error) {
          console.error('❌ Failed to share document via chat:', error);
        }
      },
    });
  };

  const handleSendFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file: File | undefined = event.target.files?.[0];
    if (!file) return;

    setIsSendingFile(true);
    setSendingFileError(null);

    const sizeLabel =
      file.size >= 1024 * 1024 ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : `${(file.size / 1024).toFixed(1)} KB`;

    const uploadPromise = (async () => {
      const providerId = waitingRoomProfile?.providerId ?? user?.id;
      const connectionId = identity;
      if (!providerId || !connectionId) {
        throw new Error(t('chatInput.connectionNotReadyError'));
      }

      const uploaded = await uploadGuestChatDocument(file, providerId, connectionId);
      return uploaded.path;
    })();

    uploadPromise
      .then(async (blobPath: string) => {
        await sendMessage(`[📎 Shared File] ${file.name}|${sizeLabel}|${blobPath}`);
      })
      .catch((e: any) => {
        if (e?.message?.includes('File too large') || e?.code === 413) {
          setSendingFileError(t('chatInput.fileSizeTooLarge'));
        } else if (e?.message?.includes('Forbidden')) {
          setSendingFileError(t('chatInput.noPermissionUpload'));
        } else {
          setSendingFileError(t('chatInput.uploadFailed'));
        }

        console.log('Sending file error: ', e);
      })
      .finally(() => {
        setIsSendingFile(false);
        if (inputFile.current) {
          inputFile.current.value = '';
        }
      });
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isRtl = i18n.dir() === 'rtl';
  const rtlTypingText =
    typingUserName && isRtl
      ? `\u2066${typingUserName}\u2069 \u2067${t('chatInput.typingVerb', {
          defaultValue: 'يكتب',
        })}\u2069\u2066${typingDots}\u2069`
      : '';

  const handleAttachClick = (event: React.MouseEvent<HTMLElement>) => {
    const roles = user?.clinicUser?.roles || [];
    const isStaff = roles.includes(ADMIN) || roles.includes(CLINIC_ADMIN) || roles.includes(CLINIC_PROVIDER);

    if (!isStaff) {
      // 🧑‍⚕️ Patient: launch file picker directly
      handleSelectFile();
      return;
    }

    // 👨‍⚕️ Admin/Provider: show MoreOptions menu
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <div className={styles.chatInput}>
      <div className={styles.statusLine}>
        {isSendingFile ? (
          <>
            <CircularProgress size={12} thickness={6} />
            <Typography component='span' variant='body6' sx={{ color: 'var(--app-text-muted)' }}>
              {t('chatInput.uploadingFile')}
            </Typography>
          </>
        ) : sendingFileError ? (
          <Typography component='span' variant='body6' className={styles.uploadError}>
            {sendingFileError}
          </Typography>
        ) : (
          typingUserName && (
            <Typography component='p' variant='body6' sx={{ color: 'var(--app-text-muted)' }}>
              {isRtl ? (
                <span dir='rtl' style={{ unicodeBidi: 'plaintext' }}>
                  {rtlTypingText.replace(typingDots, '')}
                  <span className={styles.typingDots} aria-hidden='true'>
                    {typingDots}
                  </span>
                </span>
              ) : (
                <>
                  {t('chatInput.isTyping', { name: typingUserName, dots: '' })}
                  <span className={styles.typingDots} aria-hidden='true'>
                    {typingDots}
                  </span>
                </>
              )}
            </Typography>
          )
        )}
      </div>

      <div className={styles.inputs}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleAttachClick} disabled={isSendingFile} className={styles.attachButton}>
            <AttachIcon color='primary' />
            <input type='file' hidden ref={inputFile} onChange={handleSendFile} accept={ALLOWED_FILE_TYPES} />
          </IconButton>

          {Boolean(anchorEl) && (
            <Box
              sx={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                zIndex: 20,
                mb: '4px',
              }}
            >
              <MoreOptions
                anchorEl={anchorEl}
                onClose={handleCloseMenu}
                onLocalDeviceClick={handleSelectFile}
                onDocumentsClick={handleOpenDocuments}
              />
            </Box>
          )}
        </Box>

        <TextField
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          value={input}
          className={styles.textInput}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={handleSandMessage}>
                  <SendIcon color='primary' />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
    </div>
  );
}
